import { Stack } from '@hurdl/design-system';
import { Link, useParams } from '@tanstack/react-router';
import { Store } from 'lucide-react';
import { useEffect, useState, type ReactElement } from 'react';

import {
  RESTAURANTS,
  type Restaurant,
  useMenuOperations,
} from './menus-wiring.js';
import {
  TILE_LINK_CLASS,
  Tile,
  TileHead,
  TileStat,
  TileStats,
} from './tile.js';

/**
 * The menus entry: the Restaurants whose approved items the panel publishes, each a card
 * linking to that Restaurant's reviewed menu (menus-spec R16).
 *
 * Each Restaurant card loads its reviewed-items count and shows it as a `Badge`, so the
 * list reads at a glance which Restaurant has a published menu and how large it is.
 * Clicking a card opens that Restaurant's menu. One Restaurant in this build; a second is
 * a new entry in {@link RESTAURANTS}, not a code change here.
 *
 * The one `h1` is the page's; the `main` landmark is the Layout's. The cards' links
 * resolve against the route tree, so no address literal reaches this source.
 */
export function MenusPage(): ReactElement {
  const { productKey } = useParams({ strict: false });

  return (
    <Stack gap="large">
      <h1 className="font-hurdl-display text-hurdl-heading font-hurdl-semibold">
        Restaurants
      </h1>

      <div className="grid gap-hurdl-5 md:grid-cols-2 xl:grid-cols-3">
        {RESTAURANTS.map((restaurant) => (
          <RestaurantCard
            key={restaurant.key}
            restaurant={restaurant}
            productKey={productKey ?? ''}
          />
        ))}
      </div>
    </Stack>
  );
}

/** One Restaurant as a shared {@link Tile}: its label, its published-items count, a link in. */
function RestaurantCard({
  restaurant,
  productKey,
}: {
  readonly restaurant: Restaurant;
  readonly productKey: string;
}): ReactElement {
  const operations = useMenuOperations();
  const [count, setCount] = useState<number | undefined>(undefined);

  useEffect(() => {
    let live = true;
    void operations.listReviewed(restaurant.key).then((items) => {
      if (live) setCount(items.length);
    });
    return () => {
      live = false;
    };
  }, [operations, restaurant.key]);

  return (
    <Link
      to="/products/$productKey/menus/$restaurantKey"
      params={{ productKey, restaurantKey: restaurant.key }}
      className={TILE_LINK_CLASS}
    >
      <Tile interactive>
        <TileHead
          icon={Store}
          title={restaurant.label}
          description="Its menu and its managed categories."
        />
        <TileStats>
          <TileStat label="Published" value={count} />
        </TileStats>
      </Tile>
    </Link>
  );
}
